import type { DateString } from "@pentacode/openapi";
import type { RosterData } from "../types";
import type { ActiveCell, Direction, NavGrid } from "./roster-navigation";
import { buildNavGrid, coordToCell, move, resolveCoord } from "./roster-navigation";

// Accessors the controller reads/writes on its host, called lazily at keypress time so it always
// sees current reactive state.
export interface RosterNavigationContext {
    data(): RosterData | undefined;
    filterString(): string;
    collapsed(): ReadonlySet<number>;
    dateRange(): { from: DateString; to: DateString } | null;
    // Department ids the active tab restricts navigation to; null navigates all departments.
    visibleDepartmentIds(): ReadonlySet<number> | null;
    includeUnassigned(): boolean;
    activeCell(): ActiveCell;
    setActive(cell: {
        date: DateString;
        department: number;
        employee: number | null;
        entry: string | null;
        field: string;
    }): void;
}

// Owns the grid cache and turns a WASD direction into an active-cell change. The pure grid math
// lives in ./roster-navigation; this glue holds the per-data cache and bridges to host state.
export class RosterNavigation {
    private _cache: { data: RosterData; signature: string; grid: NavGrid } | null = null;

    constructor(private readonly ctx: RosterNavigationContext) {}

    // Drop the cached grid. Needed when row order changes in place (reorder splices dep.employees
    // without swapping _data), which neither the identity nor signature check would notice.
    invalidate(): void {
        this._cache = null;
    }

    // Move the cursor one step and write the result back through setActive. No-op when nothing
    // is navigable.
    moveCursor(direction: Direction): void {
        const grid = this._grid();
        if (!grid || !grid.rows.length) {
            return;
        }
        const start = resolveCoord(grid, this.ctx.activeCell(), direction);
        const next = move(grid, start, direction);
        this.ctx.setActive({ ...coordToCell(grid, next), field: "start" });
    }

    // Grid for the current data, cached in one slot keyed by RosterData identity + a signature
    // (filter, collapse set, date range, unassigned visibility); rebuilt only when those change.
    private _grid(): NavGrid | null {
        const data = this.ctx.data();
        if (!data) {
            return null;
        }
        const collapsed = this.ctx.collapsed();
        const range = this.ctx.dateRange();
        const visibleDepartmentIds = this.ctx.visibleDepartmentIds();
        const includeUnassigned = this.ctx.includeUnassigned();
        const departments = visibleDepartmentIds ? [...visibleDepartmentIds].sort((a, b) => a - b).join(",") : "all";
        const signature = `${this.ctx.filterString()}|${[...collapsed].sort((a, b) => a - b).join(",")}|${range?.from ?? ""}_${range?.to ?? ""}|${departments}|${includeUnassigned}`;
        if (this._cache && this._cache.data === data && this._cache.signature === signature) {
            return this._cache.grid;
        }
        const grid = buildNavGrid(data, {
            filterString: this.ctx.filterString(),
            collapsed,
            dateRange: range,
            visibleDepartmentIds: visibleDepartmentIds ?? undefined,
            includeUnassigned,
        });
        this._cache = { data, signature, grid };
        return grid;
    }
}
